import { z } from 'zod';
import { VollWalletReceiptSchema } from './voll_wallet_refund.js';
import {
  VollWalletAdditionalFieldSchema,
  VollWalletAdditionalFieldValueSchema,
  VollWalletRefSchema,
} from './voll_wallet_pix.js';

/** Um gasto da carteira que espera a nota fiscal: onde, quando (dia em São Paulo), quanto, e a
 *  cobrança que ele já carrega. */
export const VollWalletPendingItemSchema = z
  .object({
    id: z.string().min(1),
    local: z.string().nullable(),
    date: z.iso.date().nullable(),
    amount: z.number(),
    category: VollWalletRefSchema.nullable(),
    motive: VollWalletRefSchema.nullable(),
  })
  .strict();
export type VollWalletPendingItem = z.infer<typeof VollWalletPendingItemSchema>;

/**
 * `GET /voll-wallet/conciliations?walletId=` — o envio da nota pendente de uma carteira: os gastos
 * que esperam nota, a alocação (só exibida, o movimento já tem a dele), as categorias e os motivos
 * para corrigir a cobrança, os campos da empresa e se a carteira aceita a autodeclaração.
 */
export const VollWalletConciliationFormSchema = z
  .object({
    items: z.array(VollWalletPendingItemSchema),
    company: VollWalletRefSchema.nullable(),
    costCenter: VollWalletRefSchema.nullable(),
    categories: z.array(VollWalletRefSchema),
    motives: z.array(VollWalletRefSchema),
    additionalFields: z.array(VollWalletAdditionalFieldSchema),
    allowSelfDeclaration: z.boolean(),
  })
  .strict();
export type VollWalletConciliationForm = z.infer<typeof VollWalletConciliationFormSchema>;

/**
 * `POST /voll-wallet/conciliations` — a nota de um gasto pendente: o comprovante já enviado OU a
 * autodeclaração (quando a carteira aceita), nunca os dois. O BFF confere que o gasto é do
 * viajante e ainda espera nota, e a categoria, o motivo e os campos da carteira.
 */
export const VollWalletConciliationRequestSchema = z
  .object({
    movementId: z.string().min(1),
    /** Ausente só quando a carteira não tem categorias. */
    categoryId: z.string().min(1).optional(),
    /** Com o nome: a lista de motivos é paginada, e o BFF confere buscando por ele. */
    motive: VollWalletRefSchema,
    receipt: VollWalletReceiptSchema.optional(),
    declaration: z
      .object({ justification: z.string().trim().min(1).max(500) })
      .strict()
      .optional(),
    observation: z.string().trim().max(300).optional(),
    additionalFields: z.array(VollWalletAdditionalFieldValueSchema),
  })
  .strict()
  .refine((r) => (r.receipt === undefined) !== (r.declaration === undefined), {
    message: 'receipt XOR declaration',
  });
export type VollWalletConciliationRequest = z.infer<typeof VollWalletConciliationRequestSchema>;

export const VollWalletConciliatedSchema = z.object({ id: z.string().min(1) }).strict();
export type VollWalletConciliated = z.infer<typeof VollWalletConciliatedSchema>;

/** As recusas que a tela explica. `movement_unavailable`: o gasto não é do viajante ou já tem
 *  nota; `additional_fields_missing`: o wallets-api pediu um campo da empresa; `conciliation_rejected`:
 *  o wallets-api não aceitou (categoria fora da carteira, movimento não atualizado). */
export const VollWalletConciliationErrorSchema = z.discriminatedUnion('error', [
  z.object({ error: z.literal('movement_unavailable') }).strict(),
  z.object({ error: z.literal('category_unavailable') }).strict(),
  z.object({ error: z.literal('motive_unavailable') }).strict(),
  z.object({ error: z.literal('declaration_unavailable') }).strict(),
  z.object({ error: z.literal('additional_fields_missing') }).strict(),
  z.object({ error: z.literal('conciliation_rejected') }).strict(),
  /** O wallets-api falhou sem recusar: não dá para afirmar que nada foi feito (#5688). */
  z.object({ error: z.literal('outcome_unknown') }).strict(),
]);
export type VollWalletConciliationError = z.infer<typeof VollWalletConciliationErrorSchema>;
